import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useLocation } from '@tanstack/react-router';
import { ChevronsLeft, ChevronsRight, Clock, FolderOpen, Tag, Upload } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys } from '~/api/queryKeys';
import { Button } from '~/components/ui/button';
import { useTaxonomyStudioStore } from '~/store/taxonomy-studio.store';
import { cn } from '~/utils/cn';

import { TaxonomyStudioContext } from '../../context/taxonomy-studio-context';
import { useReleases, useTaxonomyPermissions } from '../../hooks/use-taxonomy-draft';
import { useTaxonomyStudioState } from '../../hooks/use-taxonomy-studio-state';
import { useUndoLatest } from '../../hooks/use-taxonomy-write';
import { isAccessProblem, readProblem } from '../../utils/taxonomy-errors';
import { readPrefs, writePrefs } from '../../utils/taxonomy-prefs';
import { QueryError } from '../shared/query-error';

import { StaleItemDialog } from './stale-item-dialog';
import { StudioBlocker } from './studio-blocker';
import { VersionBar } from './version-bar';
import { WorkflowHint } from './workflow-hint';

interface RailItem {
  to: '/taxonomy/products' | '/taxonomy/attributes' | '/taxonomy/versions' | '/taxonomy/uploads';
  labelKey: string;
  icon: LucideIcon;
}

/** Icons match the identity-service SYSTEM_ADMIN seed (folder-open, tag, clock, upload). */
const RAIL_ITEMS: RailItem[] = [
  { to: '/taxonomy/products', labelKey: 'nav.products', icon: FolderOpen },
  { to: '/taxonomy/attributes', labelKey: 'nav.attributes', icon: Tag },
  { to: '/taxonomy/versions', labelKey: 'nav.versions', icon: Clock },
  { to: '/taxonomy/uploads', labelKey: 'nav.uploads', icon: Upload },
];

/** True when the key event comes from a text field (keep native text undo there). */
function isTextTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
}

/**
 * Taxonomy Studio shell: its own left rail (Product Taxonomy, Attributes, Versions &
 * History with the unpublished-change count, Bulk Upload), the shared version status
 * bar, and the page. Provides the studio context and binds Ctrl/Cmd+Z to "undo my
 * latest action". The rail's collapsed state is a per-viewer preference.
 */
export function TaxonomyStudioLayout() {
  const { t } = useTranslation('taxonomy-studio');
  const labelId = useId();
  const location = useLocation();
  const studio = useTaxonomyStudioState();
  const undoLatest = useUndoLatest();
  const [collapsed, setCollapsed] = useState(() => readPrefs().railCollapsed);
  const draftCount = studio.draft?.changeCount ?? 0;
  const qc = useQueryClient();
  const releasesQuery = useReleases();
  const permissionsQuery = useTaxonomyPermissions();
  const storeAccess = useTaxonomyStudioStore((s) => s.accessProblem);
  const storeNotInitialized = useTaxonomyStudioStore((s) => s.notInitialized);

  // Studio-wide blockers, derived from the always-on reads (releases, permissions) plus
  // whatever any other call escalated into the store: no access → one full-page message;
  // never imported → an empty state instead of a wall of empty panels.
  const releasesProblem = releasesQuery.error ? readProblem(releasesQuery.error) : null;
  const permissionsProblem = permissionsQuery.error ? readProblem(permissionsQuery.error) : null;
  const queryAccess = [releasesProblem, permissionsProblem].find(
    (p): p is NonNullable<typeof p> => p !== null && isAccessProblem(p),
  );
  const access =
    storeAccess ??
    (queryAccess
      ? { status: queryAccess.status, code: queryAccess.code, detail: queryAccess.detail }
      : permissionsQuery.data && !permissionsQuery.data.canRead
        ? { status: 403, code: 'NOT_PLATFORM_ADMIN', detail: '' }
        : null);
  const notInitialized =
    storeNotInitialized ||
    releasesProblem?.code === 'NOT_INITIALIZED' ||
    releasesQuery.data?.length === 0;
  const blocked = access !== null || notInitialized;
  const retryBlocked = () => {
    const store = useTaxonomyStudioStore.getState();
    store.setAccessProblem(null);
    store.setNotInitialized(false);
    void qc.invalidateQueries({ queryKey: queryKeys.taxonomyStudio.all });
  };
  const { isEditable } = studio;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isEditable) return;
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      if (e.key.toLowerCase() !== 'z' || isTextTarget(e.target)) return;
      // A dialog owns the keyboard while open.
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      void undoLatest();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [isEditable, undoLatest]);

  const toggleRail = () => {
    setCollapsed((prev) => {
      writePrefs({ railCollapsed: !prev });
      return !prev;
    });
  };

  return (
    <TaxonomyStudioContext.Provider value={studio}>
      <div className="bg-card flex h-full min-h-0 overflow-hidden">
        <aside
          aria-labelledby={labelId}
          className={cn(
            'border-border flex shrink-0 flex-col border-r py-4 transition-[width] duration-200',
            collapsed ? 'w-14' : 'w-60',
          )}
        >
          <p
            id={labelId}
            className={cn(
              'text-muted-foreground mb-3 px-4 text-xs font-semibold tracking-wider whitespace-nowrap uppercase',
              collapsed && 'sr-only',
            )}
          >
            {t('title')}
          </p>
          <nav className="flex-1 space-y-0.5 px-2">
            {RAIL_ITEMS.map((item) => {
              const isActive =
                location.pathname === item.to || location.pathname.startsWith(item.to + '/');
              const Icon = item.icon;
              const label = t(item.labelKey);
              const badge = item.to === '/taxonomy/versions' && draftCount > 0 ? draftCount : null;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  title={collapsed ? label : undefined}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'focus-visible:ring-ring relative flex min-h-11 cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none',
                    'hover:bg-accent hover:text-accent-foreground',
                    isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
                  )}
                >
                  <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                  <span className={cn('flex-1 truncate', collapsed && 'sr-only')}>{label}</span>
                  {badge !== null && (
                    <span
                      title={t('nav.unpublishedBadge')}
                      className={cn(
                        'bg-primary text-primary-foreground inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] tabular-nums',
                        collapsed && 'absolute top-1 right-1 h-4 min-w-4 text-[10px]',
                      )}
                    >
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="px-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground w-full cursor-pointer justify-start"
              aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
              onClick={toggleRail}
            >
              {collapsed ? <ChevronsRight /> : <ChevronsLeft />}
              {!collapsed && t('nav.collapse')}
            </Button>
          </div>
        </aside>

        <div className="bg-gradient-page flex min-h-0 min-w-0 flex-1 flex-col">
          {blocked ? (
            <StudioBlocker access={access} onRetry={retryBlocked} />
          ) : (
            <>
              <div className="shrink-0 space-y-2 px-6 pt-4">
                {releasesQuery.isError ? (
                  <QueryError
                    error={releasesQuery.error}
                    onRetry={() => {
                      void releasesQuery.refetch();
                    }}
                  />
                ) : (
                  <VersionBar />
                )}
                {studio.isEditable && (studio.draft?.changeCount ?? 0) === 0 && <WorkflowHint />}
              </div>
              <div className="min-h-0 flex-1 overflow-auto">
                <Outlet />
              </div>
            </>
          )}
        </div>
        <StaleItemDialog />
      </div>
    </TaxonomyStudioContext.Provider>
  );
}
